/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { MCQPulseLogo } from './MCQPulseLogo';

export const Footer: React.FC = () => {
  const { setPage, setAdminLoginModalOpen, user, setAuthModalOpen, setPendingPurchaseAction } = useApp();
  const [lastUploadDate, setLastUploadDate] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    fetch('/api/content/last-upload-date')
      .then((res) => res.json())
      .then((data) => {
        if (isMounted && data && data.lastUploadDate) {
          setLastUploadDate(data.lastUploadDate);
        }
      })
      .catch(() => {
        // Fail gracefully without breaking footer
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <footer className="bg-slate-900 text-slate-300 dark:bg-slate-950 border-t border-slate-800 pt-11 pb-5 transition-colors">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* Column 1: Brand & Tagline */}
          <div className="lg:col-span-3 space-y-3">
            <div className="flex items-center gap-4 cursor-pointer group animate-fade-in" onClick={() => setPage('home')}>
              <MCQPulseLogo size={54} className="group-hover:scale-105 transition-transform duration-300 animate-pulse-subtle shrink-0" />
              <div className="flex flex-col justify-center">
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-none whitespace-nowrap flex items-start">
                  <span>MCQ PULSE</span><span className="text-[10px] sm:text-xs text-sky-400 select-none align-super font-black leading-none ml-1 mt-0.5">™</span>
                </div>
                <div className="font-sans text-[10px] sm:text-xs uppercase text-sky-400 font-semibold mt-1 tracking-[3.5px]">
                  LEARNING SOLUTIONS
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-[360px] text-justify">
              <strong>MCQ PULSE™</strong> empowers Academic students with premium Study Resources, including chapter-wise MCQs, case-based questions, HOTS, assertion & reason, and previous year questions - designed to build confidence and achieve academic excellence.
            </p>
          </div>

          {/* Column 2: Academic */}
          <div className="lg:col-span-3 space-y-3 lg:pl-16">
            <h4 className="text-xs uppercase font-extrabold tracking-wider text-slate-100 font-display text-left">Academic Hub</h4>
            <ul className="space-y-1 text-sm text-slate-400">
              <li><button onClick={() => setPage('courses')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Courses</button></li>
              <li><button onClick={() => setPage('practice')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Mock Tests</button></li>
              <li><button onClick={() => setPage('blog')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Blog</button></li>
              <li><a href="https://forms.gle/sHVnVpUazAp93PQp8" target="_blank" rel="noopener noreferrer" className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Student Query/Doubt Form</a></li>
              <li><button onClick={() => setPage('courses')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Study Resources</button></li>
            </ul>
          </div>

          {/* Column 3: Community & Support */}
          <div className="lg:col-span-3 space-y-3 lg:pl-16">
            <h4 className="text-xs uppercase font-extrabold tracking-wider text-slate-100 font-display text-left">Resources & Support</h4>
            <ul className="space-y-1 text-sm text-slate-400">
              <li><button onClick={() => setPage('faq')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Frequently Asked Questions</button></li>
              <li><button onClick={() => setPage('help-center')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Help Center / Guidelines</button></li>
              <li><button onClick={() => setPage('contact')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Contact Support</button></li>
              <li><button onClick={() => {
                if (!user) {
                  setPendingPurchaseAction({ fn: () => setPage('feedback') });
                  setAuthModalOpen(true, 'login');
                } else {
                  setPage('feedback');
                }
              }} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Feedback</button></li>
              <li><button onClick={() => setPage('admin-portal')} className="hover:text-sky-400 transition-colors text-left lg:whitespace-nowrap">Admin Login</button></li>
            </ul>
          </div>

          {/* Column 4: Follow Us */}
          <div className="lg:col-span-3 space-y-3">
            <h4 className="text-xs uppercase font-extrabold tracking-wider text-slate-100 font-display">
              Follow Us On
            </h4>
            <div className="border border-slate-800 dark:border-slate-800/80 rounded-xl p-5 bg-slate-950/40 dark:bg-slate-950/60 shadow-xl flex flex-col gap-3.5">
              {/* Info Row: Icon + Texts */}
              <div className="flex items-center gap-4">
                {/* RSS / Transmission Wave Icon */}
                <div className="text-white shrink-0">
                  <svg className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.18 15.64a2.18 2.18 0 1 1-2.18 2.18 2.18 2.18 0 0 1 2.18-2.18m10.15 4.36A12.33 12.33 0 0 0 4 7.67v3a9.33 9.33 0 0 1 9.33 9.33M20 20A16 16 0 0 0 4 4v3a13 13 0 0 1 13 13z"/>
                  </svg>
                </div>
                {/* Text Block */}
                <div className="flex flex-col">
                  <span className="font-display font-bold text-sm text-white tracking-wide leading-tight">
                    Follow Us On Social Media
                  </span>
                  <span className="text-xs text-slate-400 mt-0.5 leading-snug">
                    Get Latest Update On Social Media
                  </span>
                </div>
              </div>

              {/* Icon Buttons Row */}
              <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-2.5 md:gap-3">
                {/* Facebook Button */}
                <a 
                  href="https://www.facebook.com/share/1CTSw2Eq1L/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-11 h-11 rounded-lg bg-[#3b5998] hover:bg-[#3b5998]/90 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-md"
                  aria-label="Follow us on Facebook"
                >
                  <svg className="h-5 w-5 fill-current text-white" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>

                {/* WhatsApp Button */}
                <a 
                  href="https://whatsapp.com/channel/0029VamtMLJ3gvWSgJ2KHy3r" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-11 h-11 rounded-lg bg-[#25D366] hover:bg-[#25D366]/90 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-md"
                  aria-label="Join our WhatsApp channel"
                >
                  <svg className="h-5 w-5 fill-current text-white" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.457L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.413 9.863-9.847.001-2.63-1.019-5.101-2.872-6.957C16.608 1.943 14.133.921 11.516.92 6.08.92 1.654 5.332 1.651 10.765c-.001 1.745.453 3.45 1.316 4.966L1.933 20.3l4.714-1.146zm11.373-7.56c-.3-.15-1.771-.875-2.046-.975-.275-.1-.475-.15-.675.15-.2.3-.775.975-.95 1.175-.175.2-.3.225-.65.075-1.04-.52-1.797-.96-2.512-2.185-.19-.325-.19-.56.12-.835.15-.13.3-.35.45-.525.15-.175.2-.3.3-.5.1-.2.05-.375-.025-.525-.075-.15-.675-1.625-.925-2.225-.244-.589-.496-.581-.675-.59l-.575-.01c-.2 0-.525.075-.8 3.75a2.03 2.03 0 0 0 .425 1.5c.15.2 2.725 4.16 6.6 5.83.922.398 1.644.634 2.204.811.927.292 1.77.25 2.438.15.745-.112 2.275-.93 2.59-1.83.315-.9.315-1.67.22-1.83-.095-.16-.3-.25-.6-.4z"/>
                  </svg>
                </a>

                {/* Instagram Button */}
                <a 
                  href="https://www.instagram.com/mcqpulse_yt/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-11 h-11 rounded-lg bg-[#E1306C] hover:bg-[#E1306C]/90 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-md"
                  aria-label="Follow us on Instagram"
                >
                  <svg className="h-5 w-5 fill-none stroke-current stroke-[2.5] text-white" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>

                {/* YouTube Button */}
                <a 
                  href="https://www.youtube.com/@MCQSCBSE" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-11 h-11 rounded-lg bg-[#FF0000] hover:bg-[#FF0000]/90 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-md"
                  aria-label="Subscribe to our YouTube channel"
                >
                  <svg className="h-5 w-5 fill-current text-white" viewBox="0 0 24 24">
                    <path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.11C19.517 3.545 12 3.545 12 3.545s-7.517 0-9.388.508a3.003 3.003 0 0 0-2.11 2.11C0 8.033 0 12 0 12s0 3.967.502 5.837a3.003 3.003 0 0 0 2.11 2.11c1.871.508 9.388.508 9.388.508s7.517 0 9.388-.508a3.003 3.003 0 0 0 2.11-2.11C24 15.967 24 12 24 12s0-3.967-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>

                {/* Telegram Button */}
                <a 
                  href="https://t.me/mcqpulse_telegram" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-11 h-11 rounded-lg bg-[#0088cc] hover:bg-[#0088cc]/90 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-md"
                  aria-label="Join our Telegram channel"
                >
                  <svg className="h-5 w-5 fill-current text-white" viewBox="0 0 24 24">
                    <path d="M21.905 2.224a.823.823 0 0 0-.756-.048l-20 8a.827.827 0 0 0-.083 1.517l5.222 2.015 2.148 6.985a.825.825 0 0 0 1.503.178l3.14-4.526 5.343 3.955a.827.827 0 0 0 1.298-.445l4-16a.823.823 0 0 0-.815-.631zm-13.62 13.061l-.64 4.162-1.09-3.542 10.375-8.232-8.645 7.612z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="text-[11px] text-slate-400 font-medium pt-1">
              Last Upload Date: {lastUploadDate ? formatDate(lastUploadDate) : '--'}
            </div>
          </div>

        </div>

        <div className="mt-9 pt-5 border-t border-slate-800 text-center flex flex-col md:flex-row md:justify-between items-center gap-3 sm:gap-4 text-xs text-slate-400">
          <p className="whitespace-nowrap text-slate-400">© 2026 MCQ PULSE™. All Rights Reserved.</p>
          <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 sm:gap-4 text-xs text-slate-400">
            <button onClick={() => setPage('about')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">About Us</button>
            <span className="text-slate-600 hidden sm:inline">•</span>
            <button onClick={() => setPage('terms-of-service')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">Terms of Service</button>
            <span className="text-slate-600 hidden sm:inline">•</span>
            <button onClick={() => setPage('privacy')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">Privacy Policy</button>
            <span className="text-slate-600 hidden sm:inline">•</span>
            <button onClick={() => setPage('disclaimer')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">Disclaimer</button>
            <span className="text-slate-600 hidden sm:inline">•</span>
            <button onClick={() => setPage('refund-policy')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">Cancellation & Refund Policy</button>
            <span className="text-slate-600 hidden sm:inline">•</span>
            <button onClick={() => setPage('shipping-policy')} className="hover:underline hover:text-white transition-colors whitespace-nowrap">Shipping & Delivery Policy</button>
          </div>
        </div>
        <div className="mt-4 text-center text-[11px] text-slate-400 border-t border-slate-800/60 pt-3 leading-relaxed max-w-4xl mx-auto px-2">
          MCQ PULSE™ is a trademark of its owner. Trademark registration is under process. All content, branding, course material, logos and designs are protected under applicable intellectual property laws.
        </div>
      </div>
    </footer>
  );
};
